import Icon from "../../components/ui/Icon";
import { FC, useContext, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AddIcon,
  Button,
  FlexContent,
  ModalDialog,
  TableCell,
  TableHeadCell,
  TableRow,
  useDebounce,
} from "@rothoblaas/design-system";
import Header from "../../components/ui/Header";
import Table from "../../components/ui/Table";
import { getDeleteAction, getEditAction } from "../../utils/rowActions";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import useInitializer from "../../hooks/useInitializer";
import useListFilters from "../../hooks/useListFilters";
import useI18n from "../../hooks/useTranslations";
import { SearchClient } from "../../http/types/clients";
import { useClientsApi } from "../../http/useClients";
import { PaginationQuery } from "../../types";

const AdminClients: FC = () => {
  const i18n = useI18n();
  const filters = useListFilters({ isSearchable: true });
  const aborter = useRef(new AbortController());
  const [clientToDelete, setClientToDelete] = useState<SearchClient>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useContext(NotificationDispatch);
  const clinetsApi = useClientsApi();
  const navigate = useNavigate();

  const debouncedValue = useDebounce(filters.state.q);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { data, error } = clinetsApi.useClients(query, aborter.current.signal);

  const isInitialized = useInitializer(data || error);

  if (!data && !error && !isInitialized) {
    return;
  }

  return (
    <>
      <Header
        title={i18n.translation.clients.plural}
        path={{ display: i18n.translation.clients.all }}
        buttons={
          <Button onClick={() => navigate(`/admin/clients/new`)}><Icon glyph={AddIcon} />
            {i18n.translation.common.createNew}
          </Button>
        }
      />

      <FlexContent padding noPaddingTop>
        <Table<SearchClient>
          filters={filters}
          search={{
            placeholder: i18n.translation.clients.search,
            onChange: () => {
              aborter.current.abort();
              aborter.current = new AbortController();
            },
          }}
          head={
            <TableRow>
              <TableHeadCell align="left">{i18n.translation.common.name}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.permissions.plural}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.secrets.plural}</TableHeadCell>
              <TableHeadCell></TableHeadCell>
            </TableRow>
          }
          items={data?.items}
          renderRow={(client) => (
            <>
              <TableCell>
                <Link onClick={(e) => e.stopPropagation()} to={client.id}>
                  {client.name}
                </Link>
              </TableCell>
              <TableCell>{client.permissions}</TableCell>
              <TableCell>{client.secrets}</TableCell>
            </>
          )}
          rowActions={(client) => [
            getEditAction(i18n, () => navigate(`/admin/clients/${client.id}`)),
            getDeleteAction(i18n, () => setClientToDelete(client), true),
          ]}
          onRowClick={(client) => navigate(`/admin/clients/${client.id}`)}
          total={data?.total}
          error={!!error}
        />
        {!!clientToDelete && (
          <ModalDialog
            onClose={() => setClientToDelete(undefined)}
            title={i18n.translation.clients.delete}
            isSubmitting={isSubmitting}
            buttons={[<Button key={0} onClick={() => setClientToDelete(undefined)} variant="secondary">{i18n.translation.common.cancel}</Button>, <Button key={1} onClick={() => {
                  setIsSubmitting(true);
                  clinetsApi
                    .deleteClient(clientToDelete.id)
                    .then(() => setClientToDelete(undefined))
                    .catch((err) => dispatch(showErrorNotification(i18n, err)))
                    .finally(() => setIsSubmitting(false));
                }}>{i18n.translation.common.yes}</Button>]}
          >
            {i18n.translation.clients.questions.delete}
          </ModalDialog>
        )}
      </FlexContent>
    </>
  );
};

export default AdminClients;
